import { useState, useMemo } from "react";
import { ProcessedSalesItem } from "../hooks/useSalesByItemData";
import { formatNumber } from "../utils/format";

interface SalesByItemTableProps {
  items: ProcessedSalesItem[];
  /** "category" renders category-grouped rows: no Item ID/Type, adds Items count */
  variant?: "item" | "category";
}

type SortField =
  | "rank"
  | "item_name"
  | "display_name"
  | "item_type"
  | "itemCount"
  | "revenue"
  | "cost"
  | "grossProfit"
  | "grossMargin"
  | "quantity"
  | "revenueShare";

type SortDir = "asc" | "desc";

// Kept local (not the shared signed formatter): this table formats per-item
// gross profit, which can be negative and currently renders "$-1,234" — the
// shared formatCurrency would change that to "-$1,234".
function formatCurrency(value: number): string {
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function marginColor(margin: number): string {
  if (margin >= 50) return "text-emerald-600";
  if (margin >= 25) return "text-amber-600";
  return "text-red-600";
}

function SortIcon({
  field,
  currentField,
  currentDir,
}: {
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
}) {
  if (field !== currentField) {
    return (
      <svg
        className="w-3 h-3 text-gray-300 ml-1 inline"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5-5 5 5H5zm0 4l5 5 5-5H5z" />
      </svg>
    );
  }
  return (
    <svg
      className="w-3 h-3 text-wmp-500 ml-1 inline"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      {currentDir === "asc" ? (
        <path d="M5 12l5-5 5 5H5z" />
      ) : (
        <path d="M5 8l5 5 5-5H5z" />
      )}
    </svg>
  );
}

export function SalesByItemTable({
  items,
  variant = "item",
}: SalesByItemTableProps) {
  const isItem = variant === "item";
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("rank");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir(field === "rank" ? "asc" : "desc");
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = items;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.item_name.toLowerCase().includes(q) ||
          item.display_name.toLowerCase().includes(q),
      );
    }

    result = [...result].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDir === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return result;
  }, [items, search, sortField, sortDir]);

  const totalRevenue = useMemo(
    () => items.reduce((sum, item) => sum + item.revenue, 0),
    [items],
  );

  const thClass =
    "text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider px-4 py-2.5 cursor-pointer hover:text-gray-700 transition-colors select-none whitespace-nowrap";
  const thRightClass =
    "text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider px-4 py-2.5 cursor-pointer hover:text-gray-700 transition-colors select-none whitespace-nowrap";

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <svg
              className="w-5 h-5 text-wmp-500 flex-shrink-0"
              width={20}
              height={20}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">
              {isItem ? "All Items" : "All Categories"}
            </h2>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
              {filteredAndSorted.length.toLocaleString()}
              {search && ` / ${items.length.toLocaleString()}`}
            </span>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-400 block">Total Revenue</span>
            <span className="text-sm font-bold text-wmp-500">
              {formatCurrency(totalRevenue)}
            </span>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={
              isItem ? "Search by item ID or name..." : "Search categories..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wmp-500/20 focus:border-wmp-500/40 transition-colors"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-100">
              <th
                className={thRightClass}
                style={{ width: 60 }}
                onClick={() => handleSort("rank")}
              >
                #
                <SortIcon
                  field="rank"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              {isItem && (
                <th className={thClass} onClick={() => handleSort("item_name")}>
                  Item ID
                  <SortIcon
                    field="item_name"
                    currentField={sortField}
                    currentDir={sortDir}
                  />
                </th>
              )}
              <th
                className={thClass}
                onClick={() => handleSort("display_name")}
              >
                {isItem ? "Display Name" : "Category"}
                <SortIcon
                  field="display_name"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              {isItem ? (
                <th className={thClass} onClick={() => handleSort("item_type")}>
                  Type
                  <SortIcon
                    field="item_type"
                    currentField={sortField}
                    currentDir={sortDir}
                  />
                </th>
              ) : (
                <th
                  className={thRightClass}
                  onClick={() => handleSort("itemCount")}
                >
                  Items
                  <SortIcon
                    field="itemCount"
                    currentField={sortField}
                    currentDir={sortDir}
                  />
                </th>
              )}
              <th
                className={thRightClass}
                onClick={() => handleSort("revenue")}
              >
                Revenue
                <SortIcon
                  field="revenue"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th className={thRightClass} onClick={() => handleSort("cost")}>
                Cost
                <SortIcon
                  field="cost"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("grossProfit")}
              >
                Gross Profit
                <SortIcon
                  field="grossProfit"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("grossMargin")}
              >
                GP%
                <SortIcon
                  field="grossMargin"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("quantity")}
              >
                Quantity
                <SortIcon
                  field="quantity"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("revenueShare")}
              >
                Share
                <SortIcon
                  field="revenueShare"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.length === 0 ? (
              <tr>
                <td
                  colSpan={isItem ? 10 : 9}
                  className="px-6 py-10 text-center text-sm text-gray-400"
                >
                  {search
                    ? `No ${isItem ? "items" : "categories"} match your search.`
                    : "No sales data found."}
                </td>
              </tr>
            ) : (
              filteredAndSorted.map((item) => (
                <tr
                  key={item.item_name}
                  className="border-b border-gray-50 last:border-b-0 hover:bg-blue-50/30 transition-colors"
                >
                  <td className="px-4 py-2.5 text-xs text-gray-400 text-right tabular-nums">
                    {item.rank}
                  </td>
                  {isItem && (
                    <td className="px-4 py-2.5 text-sm font-mono text-gray-700 whitespace-nowrap">
                      {item.item_name}
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-sm text-gray-900 max-w-xs truncate">
                    {item.display_name || "—"}
                  </td>
                  {isItem ? (
                    <td className="px-4 py-2.5 text-xs text-gray-500">
                      <span className="bg-gray-100 px-2 py-0.5 rounded-full">
                        {item.item_type}
                      </span>
                    </td>
                  ) : (
                    <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
                      {formatNumber(item.itemCount ?? 0)}
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-sm font-medium text-wmp-500 text-right tabular-nums">
                    {formatCurrency(item.revenue)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 text-right tabular-nums">
                    {formatCurrency(item.cost)}
                  </td>
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-800 text-right tabular-nums">
                    {formatCurrency(item.grossProfit)}
                  </td>
                  <td
                    className={`px-4 py-2.5 text-sm font-semibold text-right tabular-nums ${marginColor(item.grossMargin)}`}
                  >
                    {item.grossMargin.toFixed(1)}%
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
                    {formatNumber(item.quantity)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 text-right tabular-nums">
                    {item.revenueShare.toFixed(1)}%
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
